import React, { useContext } from 'react';
import Button from '../Button';
import { getUnitsData, getTimelineData } from '../data';
import { LanguageContext, useNavigation } from '../LanguageContext';
import { logoMap } from '../logos/logoMap';
import { ArrowRightIcon } from '../icons/ArrowRightIcon';

const UnitCard: React.FC<{ unit: ReturnType<typeof getUnitsData>[0] }> = ({ unit }) => {
    const { navigate } = useNavigation();
    const { t, language } = useContext(LanguageContext);
    const LogoComponent = unit?.id ? logoMap[unit.id] : null;

    const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault();
        navigate(unit?.href || '/');
    };
    const href = unit?.href ? `${unit.href}${language !== 'en' ? `?lang=${language}` : ''}` : '/';

    return (
        <a
            href={href}
            onClick={handleClick}
            className="block group focus:outline-none focus:ring-2 focus:ring-accent rounded-lg"
        >
            <div className="bg-white dark:bg-black border border-gray-200 dark:border-gray-800 rounded-lg p-10 text-center flex flex-col items-center justify-between h-full group-hover:shadow-xl group-hover:border-accent transition-all duration-300">
                <div>
                    <div className="h-24 w-full flex items-center justify-center px-4 mb-4">
                        {LogoComponent ? (
                            <div className="w-full h-full flex items-center justify-center [&>svg]:w-full [&>svg]:h-full [&>svg]:object-contain text-gray-900 dark:text-gray-50 transition-transform duration-300 group-hover:scale-110"><LogoComponent /></div>) : (
                            <div className="w-32 h-12 bg-gray-200 dark:bg-gray-800 rounded-md" />
                        )}
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 break-all">{unit?.description || ''}</p>
                </div>
                <div className="mt-6 flex items-center justify-center gap-x-1.5 text-sm font-semibold text-accent group-hover:underline">
                    {t?.units?.exploreUnit || 'Explore unit'}
                    <ArrowRightIcon className="h-4 w-4 transition-transform group-hover:translate-x-1 rtl:group-hover:-translate-x-1" />
                </div>
            </div>
        </a>
    );
};

const Units: React.FC<{ openChat: (source?: string) => void }> = ({ openChat }) => {
    const { t } = useContext(LanguageContext);
    const unitsData = getUnitsData(t);
    const timelineData = getTimelineData(t);

    // Create an ordered map of unit IDs from the timeline data.
    // The timeline is the single source of truth for founding year order and tie-breaking.
    const unitOrderMap = new Map<string, number>();
    timelineData.forEach((event: any, index: number) => {
        const title = (event?.title || '').toLowerCase();
        if (title.includes('arhideko')) unitOrderMap.set('arhideko', index);
        else if (title.includes('creaton')) unitOrderMap.set('creaton', index);
        else if (title.includes('buildhub')) unitOrderMap.set('buildhub', index);
        else if (title.includes('kate interiors')) unitOrderMap.set('kateinteriors', index);
        else if (title.includes('craft & code')) unitOrderMap.set('craftcode', index);
        else if (title.includes('venturelab')) unitOrderMap.set('venturelab', index);
    });

    // Sort the unitsData array based on the order derived from the timeline.
    const sortedUnitsData = [...(unitsData || [])].sort((a, b) => {
        const orderA = unitOrderMap.get(a.id);
        const orderB = unitOrderMap.get(b.id);
        if (orderA === undefined) return 1; // Put units not in timeline at the end
        if (orderB === undefined) return -1;
        return orderA - orderB;
    });

    return (
        <section id="units" className="py-20 sm:py-32 bg-gray-50 dark:bg-gray-900/50 scroll-mt-20">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-center">
                    <h2 className="text-3xl font-bold tracking-tight sm:text-4xl text-gray-900 dark:text-gray-50">{t?.units?.title || ''}</h2>
                    <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">{t?.units?.description || ''}</p>
                </div>

                <div className="mt-16 max-w-4xl mx-auto grid grid-cols-1 sm:grid-cols-2 gap-8">
                    {sortedUnitsData.filter(Boolean).map((unit: any) => <UnitCard key={unit?.id || unit?.name} unit={unit} />)}
                </div>

                <div className="mt-20 text-center">
                    <Button onClick={() => openChat('units_cta')} variant="secondary">{t?.assistant?.title || 'AI Assistant'}</Button>
                </div>
            </div>
        </section>
    );
};

export default Units;
